import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useOutletContext} from 'react-router-dom';
import {setComma, splitDateTime} from "../../../utils/format";
import {getMemberChargeList} from "../../../api/transaction/chargeApi";
import {getMemberPayUseList} from "../../../api/transaction/payApi";
import {getMemberRefundList} from "../../../api/transaction/refundApi";
import {getMemberExpiredList} from "../../../api/transaction/expiredApi";
import {EmptyState} from "../../../components/common/EmptyStateProps";
import {OutletContextType} from "../../../types/common/oulet.comtext.types";

type SourceType = 'charge' | 'use' | 'refund' | 'expired';

interface UnifiedItem {
    id: string;
    sourceType: SourceType;
    regDate: string;
    data: any;
}

interface TypePagination {
    page: number;
    pageSize: number;
    totalSize: number;
}

// 한 번에 화면에 내려주는 개수
const DISPLAY_STEP = 5;

const TYPES: SourceType[] = ['charge', 'use', 'refund', 'expired'];

const apiFns: Record<SourceType, (p: any) => Promise<any>> = {
    charge: getMemberChargeList,
    use: getMemberPayUseList,
    refund: getMemberRefundList,
    expired: getMemberExpiredList,
};

export default function AllListPage() {
    const navigate = useNavigate();
    const {searchTypes, setTotalSize, searchTrigger} = useOutletContext<OutletContextType>();

    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [displayedItems, setDisplayedItems] = useState<UnifiedItem[]>([]);
    const [showMore, setShowMore] = useState(false);

    const sentinelRef = useRef<HTMLDivElement>(null);

    // 정렬된 채로 쌓아두는 버퍼 - 화면엔 아직 안 보인 항목들
    const buffer = useRef<UnifiedItem[]>([]);

    // 타입별 페이지 정보
    const pagination = useRef<Record<SourceType, TypePagination>>({
        charge: {page: 0, pageSize: 0, totalSize: 0},
        use: {page: 0, pageSize: 0, totalSize: 0},
        refund: {page: 0, pageSize: 0, totalSize: 0},
        expired: {page: 0, pageSize: 0, totalSize: 0},
    });

    const sortByDate = (arr: UnifiedItem[]): UnifiedItem[] =>
        [...arr].sort((a, b) => new Date(b.regDate).getTime() - new Date(a.regDate).getTime());

    const hasMoreForType = (type: SourceType): boolean => {
        const p = pagination.current[type];
        return p.totalSize > 0 && p.page * p.pageSize < p.totalSize;
    };

    const anyHasMore = (): boolean => TYPES.some(hasMoreForType);

    const processResult = (type: SourceType, res: PromiseSettledResult<any>): UnifiedItem[] => {
        if (res.status !== 'fulfilled' || !res.value?.data?.data) return [];
        const d = res.value.data.data;
        pagination.current[type] = {
            page: d.page,
            pageSize: d.pageSize || searchTypes.size || 5,
            totalSize: d.totalSize,
        };
        return (d.rows as any[]).map((r: any, i: number) => ({
            id: `${type}-${r.id ?? r.transactionCode ?? r.refundRequestCode ?? r.expirationCode ?? i}`,
            sourceType: type,
            regDate: r.regDate,
            data: r,
        }));
    };

    // 버퍼에서 count개를 꺼내 반환
    const drainBuffer = (count: number): UnifiedItem[] =>
        buffer.current.splice(0, count);

    const calcShowMore = (): boolean =>
        buffer.current.length > 0 || anyHasMore();

    // ── 검색 ───────────────────────────────────────────────
    const handleSearch = async () => {
        try {
            setLoading(true);

            // 페이지 초기화
            pagination.current = {
                charge: {page: 0, pageSize: 0, totalSize: 0},
                use: {page: 0, pageSize: 0, totalSize: 0},
                refund: {page: 0, pageSize: 0, totalSize: 0},
                expired: {page: 0, pageSize: 0, totalSize: 0},
            };
            buffer.current = [];

            const params = {...searchTypes, page: 1};
            const [chargeRes, useRes, refundRes, expiredRes] = await Promise.allSettled([
                getMemberChargeList(params),
                getMemberPayUseList(params),
                getMemberRefundList(params),
                getMemberExpiredList(params),
            ]);

            buffer.current = sortByDate([
                ...processResult('charge', chargeRes),
                ...processResult('use', useRes),
                ...processResult('refund', refundRes),
                ...processResult('expired', expiredRes),
            ]);

            // 버퍼에서 첫 DISPLAY_STEP개만 표시
            const firstBatch = drainBuffer(DISPLAY_STEP);
            setDisplayedItems(firstBatch);

            const total = TYPES.reduce((sum, t) => sum + pagination.current[t].totalSize, 0);
            setTotalSize(total);
            setShowMore(calcShowMore());
        } catch (err) {
            console.error('전체 내역 에러:', err);
        } finally {
            setLoading(false);
        }
    };

    // ── 더보기 ─────────────────────────────────────────────
    const handleLoadMore = async () => {
        try {
            setLoadingMore(true);

            // 버퍼에 충분히 남아있으면 API 호출 없이 바로 표시
            if (buffer.current.length >= DISPLAY_STEP) {
                const batch = drainBuffer(DISPLAY_STEP);
                setDisplayedItems(prev => [...prev, ...batch]);
                setShowMore(calcShowMore());
                return;
            }

            // 버퍼가 부족하면 아직 페이지 남은 타입들의 다음 페이지를 모두 가져옴
            if (anyHasMore()) {
                const results = await Promise.allSettled(
                    TYPES.map(type =>
                        hasMoreForType(type)
                            ? apiFns[type]({...searchTypes, page: pagination.current[type].page + 1})
                            : Promise.resolve(null)
                    )
                );

                const newItems: UnifiedItem[] = [];
                TYPES.forEach((type, i) => {
                    const res = results[i];
                    if (res.status === 'fulfilled' && res.value !== null) {
                        newItems.push(...processResult(type, res));
                    }
                });

                // 새 항목을 버퍼에 병합 후 정렬 (버퍼만 정렬, 이미 표시된 항목에는 영향 없음)
                buffer.current = sortByDate([...buffer.current, ...newItems]);
            }

            const batch = drainBuffer(DISPLAY_STEP);
            setDisplayedItems(prev => [...prev, ...batch]);
            setShowMore(calcShowMore());
        } catch (err) {
            console.error('더보기 에러:', err);
        } finally {
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        handleSearch();
    }, [searchTrigger]);

    // 무한스크롤: sentinel을 항상 DOM에 유지하고, 조건만 콜백 내부에서 체크
    useEffect(() => {
        if (!sentinelRef.current) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && showMore && !loadingMore && !loading) {
                    handleLoadMore();
                }
            },
            {rootMargin: '200px'}
        );
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [showMore, loadingMore, loading]);

    // ── 렌더 ───────────────────────────────────────────────
    const renderItem = (item: UnifiedItem) => {
        const {sourceType, data} = item;
        const {date, time} = splitDateTime(item.regDate);

        let statusText = '';
        let statusClass = 'status finish';
        let content = '';
        let amount = 0;
        let isPlus = false;
        let method = '';
        let onItemClick: () => void = () => {};

        if (sourceType === 'charge') {
            isPlus = data.chargeType !== 'CHARGE_CANCEL';
            statusText = isPlus ? '충전완료' : '충전취소';
            statusClass = isPlus ? 'status finish' : 'status cancel';
            content = 'WINPAY 충전';
            amount = data.amount;
            method = data.paymentMethod === 'ADJUSTMENT' ? '관리자 지급' : '계좌이체';
            onItemClick = () => navigate(`/wallet/charge-list/${data.transactionCode}`);
        } else if (sourceType === 'use') {
            const isAdjustment = data.useType === 'ADJUSTMENT';
            isPlus = !isAdjustment && data.useType === 'USE_CANCEL';
            statusText = isAdjustment ? '차감완료' : data.useType === 'USE_CANCEL' ? '결제취소' : '결제완료';
            statusClass = isPlus ? 'status cancel' : 'status finish';
            content = isAdjustment
                ? data.serviceType
                : data.adjust
                    ? '급여 조정'
                    : `서비스이용료[${data.serviceType}]`;
            amount = data.amount;
            method = data.paymentMethod;
            onItemClick = () => navigate(`/wallet/use-list/${data.transactionCode}`, {state: {useType: data.useType}});
        } else if (sourceType === 'refund') {
            const isMinus = data.status === 'COMPLETED';
            isPlus = !isMinus;
            statusText = data.statusDescription;
            statusClass = isMinus ? 'status finish' : 'status';
            content = '잔액환불';
            amount = data.refundableAmount;
            method = data.refundMethod;
            onItemClick = () => navigate(`/wallet/refund-list/${data.refundRequestCode}`);
        } else if (sourceType === 'expired') {
            isPlus = false;
            statusText = '기간만료';
            statusClass = 'status finish';
            content = '기간만료';
            amount = data.originalAmount;
            method = 'WINPAY';
            onItemClick = () => navigate(`/wallet/expired-list/${data.expirationCode}`);
        }

        return (
            <div
                key={item.id}
                className="payment-wrap"
                onClick={onItemClick}
                style={{cursor: 'pointer'}}
            >
                <div className="payment-info">
                    <div className="info-row">
                        <div className="time">
                            {date}<em className="line">{time}</em>
                        </div>
                        <div className={statusClass}>{statusText}</div>
                    </div>
                    <div className="info-row">
                        <div className="cont">{content}</div>
                        <div className={isPlus ? 'amount' : 'amount text-red'}>
                            {isPlus ? '+' : '-'}{setComma(amount)}원
                        </div>
                    </div>
                    <div className="info-row">
                        <div className="method">{method}</div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {loading ? (
                <section className="bg-white no-gap">
                    <div className="inner-box">
                        <p>데이터를 불러오는 중...</p>
                    </div>
                </section>
            ) : !displayedItems || displayedItems.length === 0 ? (
                <EmptyState message="내역이 비어있습니다."/>
            ) : (
                <section className="bg-white no-gap">
                    <div className="inner-box">
                        {displayedItems.map(renderItem)}
                    </div>
                </section>
            )}
            {/* sentinel은 항상 DOM에 유지 — 검색 후에도 옵저버가 재감지 가능 */}
            <div ref={sentinelRef} style={{padding: '16px', textAlign: 'center'}}>
                {loadingMore && <p>로딩 중...</p>}
            </div>
        </>
    );
}
